import { Instagram, Youtube } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { TikTokIcon } from "@/components/Generic/PlatformIcon";

interface SocialAccount {
  id: string;
  platform: "instagram" | "youtube" | "tiktok";
  handle: string;
  followers: number;
  posts: number;
}

interface ProfileSelectorProps {
  socialAccounts: SocialAccount[];
  selectedAccount: string;
  onAccountChange: (accountId: string) => void;
  hasConnectedAccounts: boolean;
}

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case "instagram":
      return <Instagram className="w-5 h-5 text-pink-500" />;
    case "youtube":
      return <Youtube className="w-5 h-5 text-red-500" />;
    case "tiktok":
      return <TikTokIcon className="w-5 h-5" />;
    default:
      return null;
  }
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat("en-US", { notation: "compact" }).format(num);
};

export const ProfileSelector = ({
  socialAccounts,
  selectedAccount,
  onAccountChange,
  hasConnectedAccounts,
}: ProfileSelectorProps) => {
  if (!hasConnectedAccounts) {
    return (
      <div className="flex flex-col gap-3 items-center">
        <Link to="/dashboard/settings" className="w-full">
          <Button className="w-full">
            <Settings className="w-4 h-4 mr-2" />
            Connect Social Accounts
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <Select value={selectedAccount} onValueChange={onAccountChange}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select a social media account" />
      </SelectTrigger>
      <SelectContent>
        {socialAccounts.map((account) => (
          <SelectItem key={account.id} value={account.id}>
            <div className="flex items-center gap-3">
              {getPlatformIcon(account.platform)}
              <div className="flex flex-col items-start">
                <span className="font-medium">{account.handle}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatNumber(account.followers)} followers • {account.posts}{" "}
                  posts
                </span>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
